import axios from 'axios';
import { BASE_URL } from './constants';

type QueryParams = {
  pageId: number;
  sectionName: string;
  key: string;
};

type Body = {
  pageId: number;
  sectionName: string;
  key: string;
  valueEnglish?: unknown;
  valueFrench?: unknown;
};

type Params = {
  id: number;
  body: Body;
};

type LoginCredentials = {
  email: string;
  password: string;
};

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  async (config) => {
    const user = JSON.parse(localStorage.getItem('user') ?? 'null');
    config.headers.Authorization = `Bearer ${user?.token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const pageCache: Record<string, any[]> = {};

export const fetchData = (params: QueryParams) => async () => {
  try {
    const response = await api.get('data', { params });
    const items = response.data?.data || [];
    
    // Sort items by updatedAt ascending so the last element is ALWAYS the most recently updated
    items.sort((a: any, b: any) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
    
    const lastIndex = items.length - 1;
    return items[lastIndex];
  } catch (error) {
    console.error(error);
  }
};

export const sendData = async (body: Body) => {
  try {
    const response = await api.post('data', body);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const updateData = async ({ id, body }: Params) => {
  try {
    const response = await api.patch(`data/${id}`, body);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const uploadFile = async (file: File) => {
  try {
    const response = await api.post(
      'file-upload',
      { file },
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data?.url;
  } catch (error) {
    console.error(error);
  }
};

export const getPageIdByName = async (pageName: string): Promise<number | null> => {
  try {
    const lowerCasePageName = pageName.toLowerCase();

    // Check if all pages are already in the cache
    if (pageCache['allPages']) {
      const cachedPages = pageCache['allPages'];
      const matchingPage = cachedPages.find(
        (page) => page.name.toLowerCase() === lowerCasePageName
      );
      return matchingPage ? matchingPage.id : null;
    }

    // If not in cache, make the API call
    const response = await api.get('page');
    if (response.data && Array.isArray(response.data.allPages)) {
      const pages = response.data.allPages;
      // Store the entire pages array in the cache
      pageCache['allPages'] = pages;
      const matchingPage = pages.find((page) => page.name.toLowerCase() === lowerCasePageName);
      return matchingPage ? matchingPage.id : null;
    }
    return null;
  } catch (error) {
    console.error('Error fetching pages:', error);
    return null;
  }
};

export const logIn = async (body: LoginCredentials) => {
  try {
    const response = await api.post('user/login', body);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const verifyUser = async (): Promise<{ user: any }> => {
  try {
    const response = await api.get('/user/me');
    return { user: response.data };
  } catch (error) {
    return { user: undefined };
  }
};
